import React, { useState, useEffect } from 'react';
import fetchData from '../../functions/fetchData';
import updateData from '../../functions/updateData';
import Layout from '../../layout/Layout';
import './ExpenseHead.css';

const ExpenseHead = () => {
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [expenseSubHeads, setExpenseSubHeads] = useState([]);
  const [activeTab, setActiveTab] = useState('heads');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states for expense head
  const [headFormData, setHeadFormData] = useState({
    name: ''
  });
  
  // Form states for expense sub head
  const [subHeadFormData, setSubHeadFormData] = useState({
    mainhead: '',
    subhead: '',
    ob: 0
  });

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState(''); // 'head' or 'subhead'
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Clear messages after 3 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch expense heads
  const fetchExpenseHeads = async () => {
    try {
      setLoading(true);
      await fetchData('expense_ledger', setExpenseHeads, 'id', {});
    } catch (error) {
      console.error('Error fetching expense heads:', error);
      setError('Failed to fetch expense heads');
    } finally {
      setLoading(false);
    }
  };

  // Fetch expense sub heads
  const fetchExpenseSubHeads = async () => {
    try {
      setLoading(true);
       await fetchData('expense_subhead', setExpenseSubHeads, 'id', {});
      
    } catch (error) {
      console.error('Error fetching expense sub heads:', error);
      setError('Failed to fetch expense sub heads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseHeads();
    fetchExpenseSubHeads();
  }, []);

  // Handle expense head form submission
  const handleHeadSubmit = async (e) => {
    e.preventDefault();
    if (!headFormData.name.trim()) {
      setError('Please enter expense head name');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await updateData('expense_ledger', headFormData);
      if (response && response.success) {
        setSuccess('Expense head added successfully!');
        setHeadFormData({ name: '' });
        fetchExpenseHeads();
      } else {
        setError('Error adding expense head');
      }
    } catch (error) {
      console.error('Error submitting expense head:', error);
      setError('Error adding expense head');
    } finally {
      setLoading(false);
    }
  };

  // Handle expense sub head form submission
  const handleSubHeadSubmit = async (e) => {
    e.preventDefault();
    if (!subHeadFormData.mainhead || !subHeadFormData.subhead.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await updateData('expense_subhead', subHeadFormData);
      if (response && response.success) {
        setSuccess('Expense sub head added successfully!');
        setSubHeadFormData({ mainhead: '', subhead: '', ob: 0 });
        fetchExpenseSubHeads();
      } else {
        setError('Error adding expense sub head');
      }
    } catch (error) {
      console.error('Error submitting expense sub head:', error);
      setError('Error adding expense sub head');
    } finally {
      setLoading(false);
    }
  };

  // Get sub heads for a specific main head
  const getSubHeadsForMainHead = (mainHead) => {
    return expenseSubHeads.filter(sub => sub.mainhead === mainHead);
  };

  // Edit handlers
  const handleEditHead = (head) => {
    setEditType('head');
    setEditingItem(head);
    setEditFormData({
      id: head.id,
      name: head.name
    });
    setShowEditModal(true);
  };

  const handleEditSubHead = (subHead) => {
    setEditType('subhead');
    setEditingItem(subHead);
    setEditFormData({
      id: subHead.id,
      mainhead: subHead.mainhead,
      subhead: subHead.subhead,
      ob: subHead.ob
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditType('');
    setEditingItem(null);
    setEditFormData({});
  };

  // Update functions
  const handleUpdateHead = async (e) => {
    e.preventDefault();
    if (!editFormData.name?.trim()) {
      setError('Please enter expense head name');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const updateFields = { name: editFormData.name };
      const whereCondition = { id: editFormData.id };
      
      const response = await updateData('expense_ledger', updateFields, whereCondition);
      if (response && response.success) {
        setSuccess('Expense head updated successfully!');
        handleCloseEditModal();
        fetchExpenseHeads();
      } else {
        setError('Error updating expense head');
      }
    } catch (error) {
      console.error('Error updating expense head:', error);
      setError('Error updating expense head');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubHead = async (e) => {
    e.preventDefault();
    if (!editFormData.mainhead || !editFormData.subhead?.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const updateFields = {
        mainhead: editFormData.mainhead,
        subhead: editFormData.subhead,
        ob: parseInt(editFormData.ob) || 0
      };
      const whereCondition = { id: editFormData.id };
      
      const response = await updateData('expense_subhead', updateFields, whereCondition);
      if (response && response.success) {
        setSuccess('Expense sub head updated successfully!');
        handleCloseEditModal();
        fetchExpenseSubHeads();
      } else {
        setError('Error updating expense sub head');
      }
    } catch (error) {
      console.error('Error updating expense sub head:', error);
      setError('Error updating expense sub head');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid expense-head-container">
        {/* Alert Messages */}
        {error && (
          <div className="alert alert-danger alert-custom">
            <i className="fa fa-exclamation-triangle"></i> {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-custom">
            <i className="fa fa-check-circle"></i> {success}
          </div>
        )}

      <div className="row">
        <div className="col-lg-12">
          <div className="panel panel-default card-view">
            <div className="panel-heading">
              <div className="pull-left">
                <h6 className="panel-title txt-dark">
                  <i className="fa fa-list-alt"></i> Expense Management
                </h6>
              </div>
              <div className="pull-right">
                <div className="expense-stats">
                  <small>
                    <strong>{expenseHeads.length}</strong> Heads | 
                    <strong> {expenseSubHeads.length}</strong> Sub Heads
                  </small>
                </div>
              </div>
              <div className="clearfix"></div>
            </div>
            
            <div className="panel-wrapper collapse in">
              <div className="panel-body">
                
                {/* Tab Navigation */}
                <div className="tab-struct custom-tab-1 custom-tabs">
                  <ul role="tablist" className="nav nav-tabs" id="myTabs_7">
                    <li className={activeTab === 'heads' ? 'active' : ''}>
                      <a 
                        href="#expense-heads" 
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('heads');
                        }}
                        role="tab"
                      >
                        <span className="visible-xs"><i className="fa fa-home"></i></span>
                        <span className="hidden-xs">
                          <i className="fa fa-list"></i> Expense Heads ({expenseHeads.length})
                        </span>
                      </a>
                    </li>
                    <li className={activeTab === 'subheads' ? 'active' : ''}>
                      <a 
                        href="#expense-subheads" 
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('subheads');
                        }}
                        role="tab"
                      >
                        <span className="visible-xs"><i className="fa fa-user"></i></span>
                        <span className="hidden-xs">
                          <i className="fa fa-sitemap"></i> Expense Sub Heads ({expenseSubHeads.length})
                        </span>
                      </a>
                    </li>
                  </ul>
                  
                  <div className="tab-content expense-tab-content" id="myTabContent_7">
                    
                    {/* Expense Heads Tab */}
                    <div 
                      className={`tab-pane fade ${activeTab === 'heads' ? 'active in' : ''}`} 
                      id="expense-heads"
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-section">
                            <h6 className="panel-title txt-dark">
                              <i className="fa fa-plus-circle"></i> Add New Expense Head
                            </h6>
                            <form onSubmit={handleHeadSubmit}>
                              <div className="form-group">
                                <label className="control-label">Expense Head Name *</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={headFormData.name}
                                  onChange={(e) => setHeadFormData({...headFormData, name: e.target.value})}
                                  placeholder="e.g., Office, Kitchen, Repair"
                                  required 
                                />
                              </div>
                              <div className="form-group">
                                <button 
                                  type="submit" 
                                  className="btn btn-submit btn-block"
                                  disabled={loading}
                                >
                                  {loading && <span className="loading-spinner"></span>}
                                  <i className="fa fa-plus"></i>
                                  <span className="btn-text">
                                    {loading ? ' Adding...' : ' Add Expense Head'}
                                  </span>
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                        
                        <div className="col-md-8">
                          <div className="table-section">
                            <h6 className="panel-title txt-dark">
                              <i className="fa fa-table"></i> Existing Expense Heads
                            </h6>
                            <div className="table-wrap">
                              <div className="table-responsive">
                                <table className="table table-hover table-striped expense-head-table">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Name</th>
                                      <th>Sub Heads Count</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expenseHeads.length === 0 ? (
                                      <tr>
                                        <td colSpan="4" className="empty-state">
                                          <i className="fa fa-folder-open"></i>
                                          <h5>No expense heads found</h5>
                                          <p>Add your first expense head using the form</p>
                                        </td>
                                      </tr>
                                    ) : (
                                      expenseHeads.map((head, index) => (
                                        <tr key={head.id}>
                                          <td>{index + 1}</td>
                                          <td>
                                            <strong>{head.name}</strong>
                                          </td>
                                          <td>
                                            <span className="badge badge-info">
                                              {getSubHeadsForMainHead(head.name).length}
                                            </span>
                                          </td>
                                          <td className="action-buttons">
                                            <button 
                                              className="btn btn-info btn-xs" 
                                              title="Edit"
                                              onClick={() => handleEditHead(head)}
                                            >
                                              <i className="fa fa-edit"></i>
                                            </button>
                                            <button className="btn btn-danger btn-xs" title="Delete">
                                              <i className="fa fa-trash"></i>
                                            </button>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expense Sub Heads Tab */}
                    <div 
                      className={`tab-pane fade ${activeTab === 'subheads' ? 'active in' : ''}`} 
                      id="expense-subheads"
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-section">
                            <h6 className="panel-title txt-dark">
                              <i className="fa fa-plus-circle"></i> Add New Expense Sub Head
                            </h6>
                            <form onSubmit={handleSubHeadSubmit}>
                              <div className="form-group">
                                <label className="control-label">Main Head *</label>
                                <select 
                                  className="form-control" 
                                  value={subHeadFormData.mainhead}
                                  onChange={(e) => setSubHeadFormData({...subHeadFormData, mainhead: e.target.value})}
                                  required
                                >
                                  <option value="">Select Main Head</option>
                                  {expenseHeads.map((head) => (
                                    <option key={head.id} value={head.name}>
                                      {head.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="control-label">Sub Head Name *</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={subHeadFormData.subhead}
                                  onChange={(e) => setSubHeadFormData({...subHeadFormData, subhead: e.target.value})}
                                  placeholder="e.g., Stationary, Gas, Building repair"
                                  required 
                                />
                              </div>
                              <div className="form-group">
                                <label className="control-label">Opening Balance</label>
                                <input 
                                  type="number" 
                                  className="form-control" 
                                  value={subHeadFormData.ob}
                                  onChange={(e) => setSubHeadFormData({...subHeadFormData, ob: parseInt(e.target.value) || 0})}
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div className="form-group">
                                <button 
                                  type="submit" 
                                  className="btn btn-submit btn-block"
                                  disabled={loading || expenseHeads.length === 0}
                                >
                                  {loading && <span className="loading-spinner"></span>}
                                  <i className="fa fa-plus"></i>
                                  <span className="btn-text">
                                    {loading ? ' Adding...' : ' Add Sub Head'}
                                  </span>
                                </button>
                              </div>
                              {expenseHeads.length === 0 && (
                                <div className="alert alert-warning">
                                  <small>
                                    <i className="fa fa-info-circle"></i> 
                                    Please add expense heads first
                                  </small>
                                </div>
                              )}
                            </form>
                          </div>
                        </div>
                        
                        <div className="col-md-8">
                          <div className="table-section">
                            <h6 className="panel-title txt-dark">
                              <i className="fa fa-table"></i> Existing Expense Sub Heads
                            </h6>
                            <div className="table-wrap expense-table-panel">
                              <div className="table-responsive">
                                <table className="table table-hover table-striped expense-head-table">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Main Head</th>
                                      <th>Sub Head</th>
                                      <th>Opening Balance</th>
                                      <th>Created</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expenseSubHeads.length === 0 ? (
                                      <tr>
                                        <td colSpan="6" className="empty-state">
                                          <i className="fa fa-sitemap"></i>
                                          <h5>No expense sub heads found</h5>
                                          <p>Add your first expense sub head using the form</p>
                                        </td>
                                      </tr>
                                    ) : (
                                      expenseSubHeads.map((subHead, index) => (
                                        <tr key={subHead.id}>
                                          <td>{index + 1}</td>
                                          <td>
                                            <span className="main-head-badge">{subHead.mainhead}</span>
                                          </td>
                                          <td><strong>{subHead.subhead}</strong></td>
                                          <td>
                                            {subHead.ob > 0 ? (
                                              <span className="text-success">฿{subHead.ob}</span>
                                            ) : (
                                              <span className="text-muted">฿0</span>
                                            )}
                                          </td>
                                          <td>{new Date(subHead.createdtm).toLocaleDateString()}</td>
                                          <td className="action-buttons">
                                            <button 
                                              className="btn btn-info btn-xs" 
                                              title="Edit"
                                              onClick={() => handleEditSubHead(subHead)}
                                            >
                                              <i className="fa fa-edit"></i>
                                            </button>
                                            <button className="btn btn-danger btn-xs" title="Delete">
                                              <i className="fa fa-trash"></i>
                                            </button>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Edit Modal */}
    {showEditModal && (
      <div className="modal fade in" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-backdrop fade in"></div>
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <i className="fa fa-edit"></i> Edit {editType === 'head' ? 'Expense Head' : 'Expense Sub Head'}
              </h4>
              <button 
                type="button" 
                className="close" 
                onClick={handleCloseEditModal}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {editType === 'head' ? (
                <form onSubmit={handleUpdateHead}>
                  <div className="form-group">
                    <label className="control-label">Expense Head Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      placeholder="Enter expense head name"
                      required 
                    />
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-default" 
                      onClick={handleCloseEditModal}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading && <span className="loading-spinner"></span>}
                      <i className="fa fa-save"></i>
                      {loading ? ' Updating...' : ' Update Head'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleUpdateSubHead}>
                  <div className="form-group">
                    <label className="control-label">Main Head *</label>
                    <select 
                      className="form-control" 
                      value={editFormData.mainhead || ''}
                      onChange={(e) => setEditFormData({...editFormData, mainhead: e.target.value})}
                      required
                    >
                      <option value="">Select Main Head</option>
                      {expenseHeads.map((head) => (
                        <option key={head.id} value={head.name}>
                          {head.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="control-label">Sub Head Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editFormData.subhead || ''}
                      onChange={(e) => setEditFormData({...editFormData, subhead: e.target.value})}
                      placeholder="Enter sub head name"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label className="control-label">Opening Balance</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={editFormData.ob || 0}
                      onChange={(e) => setEditFormData({...editFormData, ob: parseInt(e.target.value) || 0})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-default" 
                      onClick={handleCloseEditModal}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading && <span className="loading-spinner"></span>}
                      <i className="fa fa-save"></i>
                      {loading ? ' Updating...' : ' Update Sub Head'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    </Layout>
  );
};

export default ExpenseHead;
